<script lang="ts">
import StepperComponent from "@/components/atoms/stepper/Stepper.vue";
import { Component, Prop, Vue, Watch } from "vue-facing-decorator";
import PriceLevelComponent from "./PriceLevel.vue";
import SingleTicketTypeSelectionComponent from "./SingleTicketTypeSelection.vue";
import { Hold, PriceLevel, PromotionHold } from "webshop-api";
import { PerformanceQASelectors, TranslationKey } from "webshop-modules";
import { useBestSeatStore } from "@/domain/ticket-selection/application/BestSeat.store";
import { usePromotionStore } from "@/domain/ticket-selection";
import { ArrayHelper } from "webshop-modules/helper/Array.helper";

@Component({
  name: 'BestSeatSelectionComponent',
  components: {
    PriceLevelComponent,
    SingleTicketTypeSelectionComponent,
    StepperComponent,
  }
})
export default class BestSeatSelectionComponent extends Vue {

  @Prop() public data!: Array<PriceLevel>;
  @Prop() public eventName!: string;

  public readonly Translation: typeof TranslationKey = TranslationKey;
  public readonly PerformanceQASelectors: typeof PerformanceQASelectors = PerformanceQASelectors;

  private readonly bestSeatStore = useBestSeatStore();

  private readonly promotionStore = usePromotionStore();

  @Watch('promotionStore.activePromotion')
    public activatePromotion(): void {
      this.bestSeatStore.activatePromotion(this.promotionStore.activePromotion?.id ?? 0);
  }

  public get isPromotionWithoutAvailability(): boolean {
    if (this.promotionStore.activePromotion === null) {
      return false;
    }

    const activePromotionHoldIndex = this.bestSeatStore.priceLevels
        .filter(pl => pl.promotionHolds)
        .reduce((agg: number[], pl) => agg.concat(pl.promotionHolds.map(ph => ph.id)), [])
        .indexOf(this.promotionStore.activePromotion.id!);

    return activePromotionHoldIndex === -1;
  }

  public get isSingleTicketTypeSelection(): boolean {
    if (!this.data || (this.data && this.data.length <= 1)) {
      return false;
    }

    const singleTicketTypes = this.data.reduce((maxTicketTypes: number, priceLevel: PriceLevel) => {
      const promotionHoldLevelCount = priceLevel.promotionHolds!.reduce((promotionHoldCount: number, promotionHold: PromotionHold) => {
        const holdLevelCount = promotionHold.holds!.reduce((holdCount: number, hold: Hold) => {
          return holdCount > hold.ticketTypes!.length ? holdCount : hold.ticketTypes!.length;
        }, 0);
        return promotionHoldCount > holdLevelCount ? promotionHoldCount : holdLevelCount;
      }, 0);
      return maxTicketTypes > promotionHoldLevelCount ? maxTicketTypes : promotionHoldLevelCount;
    }, 0);

    if (singleTicketTypes !== 1) {
      return false;
    }

    // Retrieves list (array) of all unique step configurations for priceList
    const stepConfigList = this.data.filter(ArrayHelper.ValidResults)
        .map(l => {
          if (!l.promotionHolds) { return undefined; }

          return l.promotionHolds
              .map(p => p.holds!.find(h => h.number === 0)).filter(ArrayHelper.ValidResults)
              .map(h => h!.ticketTypes).filter(ArrayHelper.ValidResults)
              .map(tt => tt!.find((tt, i) => i === 0)).filter(ArrayHelper.ValidResults)
              .map(tt => tt!.onlyInGroupsOfPerOrder ? tt!.onlyInGroupsOfPerOrder : 1)
              .reduce((prev: number[], cur: number) => ArrayHelper.ToUniqueValues(prev, cur), []);
        }).filter(ArrayHelper.ValidResults)
        .reduce(ArrayHelper.ToSingleArray, [])!
        .reduce((prev: number[], cur: number) => ArrayHelper.ToUniqueValues(prev, cur), []);

    return stepConfigList.length === 1;
  }

}
</script>

<template>
  <div id="best-seat-selection" class="o-seatSelection" :data-qa="PerformanceQASelectors.BEST_SEAT_SELECTION">
    <template v-if="isSingleTicketTypeSelection">
      <SingleTicketTypeSelectionComponent :data="data" :eventName="eventName"/>
    </template>

    <template v-else-if="isPromotionWithoutAvailability">
      <div class="m-section">
        <div class="m-blockNotice -warning">
          <div class="m-blockNotice__content">
            <p v-translate="{ key: Translation.Notice_promotion_prices_not_available }">Tickets for activated promotion are currently not available.</p>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <PriceLevelComponent v-for="priceLevel in data"
                           :data="priceLevel"
                           :eventName="eventName"
                           :key="priceLevel.priceLevelId" />
    </template>
  </div>
</template>
